import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useTrashedServices,
  useRestoreServices,
  usePermanentlyDeleteServices,
  useForceDeleteServices,
  useActiveServiceTypes,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { AlertDialog } from '../../components/ui/AlertDialog';
import { defaultUnitLabel } from '../../utils/servicePricing';
import { getServiceDependencyLink, humanizeTableName } from '../../utils/serviceDependencyLinks';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  RotateCcw,
  Trash2,
  ArrowLeft,
  Tags,
  Zap,
} from 'lucide-react';

const LIMIT = 20;

export function ServicesTrash() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<{ mode: 'single' | 'bulk'; service?: any } | null>(null);
  const [forceTarget, setForceTarget] = useState<{ ids: string[]; label: string } | null>(null);
  const [blockedDialog, setBlockedDialog] = useState<{ title: string; ids: string[]; messages: ReactNode[] } | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useTrashedServices(page, LIMIT, debouncedSearch.trim() || undefined);
  const restoreServices = useRestoreServices();
  const permanentDelete = usePermanentlyDeleteServices();
  const forceDelete = useForceDeleteServices();

  // Resolve the stored serviceType code to its managed label (falls back to the
  // raw code for legacy values that are not in the catalogue).
  const { data: serviceTypesData } = useActiveServiceTypes();
  const serviceTypeName = (code?: string) =>
    serviceTypesData?.data?.find((type) => type.code === code)?.name || code || '—';

  const services = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  const serviceName = (service: any) => service?.name || service?.code || 'Unknown';

  /**
   * Build a blocked-delete bullet that tells the admin exactly which record
   * type is holding the service and links straight to the page where those
   * records can be reviewed or removed.
   */
  const blockedMessage = (label: string, reason: string, table?: string): ReactNode => {
    const link = getServiceDependencyLink(table);
    return (
      <span>
        {label} — {reason}{' '}
        {link ? (
          <Link to={link.path} className="text-primary font-medium hover:underline whitespace-nowrap">
            Open {link.label} →
          </Link>
        ) : table ? (
          <span className="text-text-muted">Dependent table: {humanizeTableName(table)}.</span>
        ) : null}
      </span>
    );
  };

  const allSelected = services.length > 0 && services.every((s: any) => selectedIds.includes(s.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !services.some((s: any) => s.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...services.map((s: any) => s.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleRestore = async (id: string) => {
    try {
      const res: any = await restoreServices.mutateAsync([id]);
      const blocked = res?.data?.blocked ?? [];
      if (blocked.length > 0) {
        toast.error(blocked[0]?.reason || 'This service could not be restored');
        return;
      }
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      toast.success('Service restored');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore service');
    }
  };

  const handleBulkRestore = async () => {
    try {
      const res: any = await restoreServices.mutateAsync(selectedIds);
      const restored = res?.data?.restored?.length ?? 0;
      const blocked = res?.data?.blocked ?? [];
      if (restored > 0) toast.success(`Restored ${restored} service${restored === 1 ? '' : 's'}`);
      if (blocked.length > 0) {
        toast.error(`${blocked.length} service(s) could not be restored: ${blocked[0]?.reason ?? ''}`);
      } else if (restored === 0) {
        toast.error('No services were restored');
      }
      setSelectedIds([]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore services');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        const res: any = await permanentDelete.mutateAsync([confirmTarget.service.id]);
        const blocked = res?.data?.blocked ?? [];
        if (blocked.length > 0) {
          setBlockedDialog({
            title: 'Cannot delete permanently',
            ids: [confirmTarget.service.id],
            messages: [
              blockedMessage(
                serviceName(confirmTarget.service),
                blocked[0]?.reason ?? 'This service could not be permanently deleted.',
                blocked[0]?.table,
              ),
            ],
          });
          return;
        }
        setSelectedIds((prev) => prev.filter((x) => x !== confirmTarget.service.id));
        toast.success('Service permanently deleted');
      } else {
        const res: any = await permanentDelete.mutateAsync(selectedIds);
        const deleted = res?.data?.deleted?.length ?? 0;
        const blocked = res?.data?.blocked ?? [];
        if (deleted > 0) toast.success(`Permanently deleted ${deleted} service${deleted === 1 ? '' : 's'}`);
        if (blocked.length > 0) {
          const nameById = new Map<string, string>(services.map((s: any) => [s.id, serviceName(s)]));
          setBlockedDialog({
            title: `Cannot permanently delete ${blocked.length} service${blocked.length === 1 ? '' : 's'}`,
            ids: blocked.map((b: any) => b.id),
            messages: blocked.map((b: any) =>
              blockedMessage(nameById.get(b.id) ?? b.id, b.reason, b.table),
            ),
          });
        } else if (deleted === 0) {
          setBlockedDialog({
            title: 'No services were deleted',
            ids: selectedIds,
            messages: ['None of the selected services could be permanently deleted.'],
          });
        }
        setSelectedIds([]);
      }
    } catch (err: any) {
      setBlockedDialog({
        title: 'Cannot delete permanently',
        ids: confirmTarget.mode === 'single' ? [confirmTarget.service.id] : selectedIds,
        messages: [err?.response?.data?.message || 'The service(s) could not be permanently deleted.'],
      });
    } finally {
      setConfirmTarget(null);
    }
  };

  const handleConfirmForceDelete = async () => {
    if (!forceTarget) return;
    const ids = forceTarget.ids;
    try {
      const res: any = await forceDelete.mutateAsync(ids);
      const deleted = res?.data?.deleted?.length ?? 0;
      const blocked = res?.data?.blocked ?? [];
      const purged: Record<string, number> = res?.data?.purged ?? {};
      const purgedTotal = Object.values(purged).reduce((sum, n) => sum + (n || 0), 0);

      if (deleted > 0) {
        toast.success(
          `Deleted ${deleted} service${deleted === 1 ? '' : 's'} and ${purgedTotal} linked record${purgedTotal === 1 ? '' : 's'}`,
        );
      }
      if (blocked.length > 0) {
        const nameById = new Map<string, string>(services.map((s: any) => [s.id, serviceName(s)]));
        setBlockedDialog({
          title: `Could not delete ${blocked.length} service${blocked.length === 1 ? '' : 's'} anyway`,
          ids: blocked.map((b: any) => b.id),
          messages: blocked.map((b: any) =>
            blockedMessage(nameById.get(b.id) ?? b.id, b.reason, b.table),
          ),
        });
      } else if (deleted === 0) {
        setBlockedDialog({
          title: 'No services were deleted',
          ids,
          messages: ['None of the selected services could be deleted.'],
        });
      }
      setSelectedIds((prev) => prev.filter((x) => !ids.includes(x)));
    } catch (err: any) {
      setBlockedDialog({
        title: 'Cannot delete anyway',
        ids,
        messages: [err?.response?.data?.message || 'The service(s) could not be deleted.'],
      });
    } finally {
      setForceTarget(null);
    }
  };

  const busy = restoreServices.isPending || permanentDelete.isPending || forceDelete.isPending;

  const blockedIds = blockedDialog?.ids ?? [];

  const openForceDelete = () => {
    if (blockedIds.length === 0) return;
    setForceTarget({
      ids: blockedIds,
      label: blockedIds.length === 1
        ? serviceName(services.find((s: any) => s.id === blockedIds[0]))
        : `${blockedIds.length} services`,
    });
    setBlockedDialog(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/administration/services')}
          className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-danger/10 rounded-lg">
            <Trash2 className="w-5 h-5 text-danger" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text">Services Trash</h1>
            <p className="text-xs text-text-muted">Restore services or delete them permanently</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search trashed services..."
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {restoreServices.isPending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Restore
            </button>
            <button
              onClick={() => setConfirmTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {permanentDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete permanently
            </button>
            <button
              onClick={() =>
                setForceTarget({
                  ids: [...selectedIds],
                  label: `${selectedIds.length} service${selectedIds.length === 1 ? '' : 's'}`,
                })
              }
              disabled={busy}
              className="px-3 py-1.5 rounded-lg border border-danger text-danger text-sm font-medium hover:bg-danger/10 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              title="Delete even if linked to other records, removing them too"
            >
              {forceDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              Delete anyway
            </button>
            <button
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed services</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={services.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {services.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center">
                      <Tags className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">
                        {search ? 'No trashed services match your search' : 'Trash is empty'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  services.map((service: any) => (
                    <tr key={service.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(service.id)}
                          onChange={() => toggleSelectOne(service.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-text">{serviceName(service)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-text">{service.code || '—'}</td>
                      <td className="px-4 py-3 text-sm text-text">{serviceTypeName(service.serviceType)}</td>
                      <td className="px-4 py-3 text-sm text-text">
                        ৳{service.price?.toLocaleString()}
                        {service.billingUnit && service.billingUnit !== 'flat' && (
                          <span className="text-text-muted">
                            {' '}/ {service.unitLabel?.trim() || defaultUnitLabel(service.billingUnit)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {service.deletedAt ? new Date(service.deletedAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleRestore(service.id)}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                            title="Restore"
                          >
                            <RotateCcw size={16} />
                          </button>
                          <button
                            onClick={() => setConfirmTarget({ mode: 'single', service })}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Delete permanently"
                          >
                            <Trash2 size={16} />
                          </button>
                          <button
                            onClick={() => setForceTarget({ ids: [service.id], label: serviceName(service) })}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Delete anyway (removes all linked records)"
                          >
                            <Zap size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {meta && (
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPageChange={setPage}
              itemName="trashed services"
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.mode === 'bulk' ? 'Delete services permanently' : 'Delete service permanently'}
        description={
          confirmTarget?.mode === 'bulk' ? (
            `Permanently delete ${selectedIds.length} selected service(s)? This cannot be undone.`
          ) : (
            <>
              Permanently delete <strong>{confirmTarget?.service ? serviceName(confirmTarget.service) : ''}</strong>?
              This cannot be undone. Linked invoices, charge events and diagnostic tests will block the delete.
            </>
          )
        }
        confirmLabel="Delete permanently"
        loading={busy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />

      <ConfirmDialog
        open={forceTarget !== null}
        title="Delete anyway — all linked records"
        description={
          <>
            This will permanently delete <strong>{forceTarget?.label ?? 'the selected service(s)'}</strong> and every
            record linked to them — invoices, charge events, lab/radiology orders, diagnostic tests and beds. This action
            cannot be undone.
          </>
        }
        confirmLabel="Delete anyway"
        loading={forceDelete.isPending}
        onConfirm={handleConfirmForceDelete}
        onCancel={() => setForceTarget(null)}
      />

      <AlertDialog
        open={blockedDialog !== null}
        title={blockedDialog?.title ?? 'Cannot delete permanently'}
        description="These records are still referenced by other data, so they cannot be permanently deleted the normal way. You can delete them anyway below — this also removes every linked record."
        messages={blockedDialog?.messages}
        actionLabel={blockedIds.length > 0 ? 'Delete Anyway' : undefined}
        onAction={blockedIds.length > 0 ? openForceDelete : undefined}
        onClose={() => setBlockedDialog(null)}
      />
    </div>
  );
}
